import { createContext, FC, useContext, useEffect, useState } from 'react';
import GlobalContext, { EAppUiState } from './GlobalContext';
import { validatePassword } from '../util/validation/passwordValidation';
import { useLocation } from 'react-router';
import API from '../api/api';
import { TResetPasswordPost } from '../api/types';
import { EQueryStringParam } from '../util/constants';

export type TResetPasswordRouterState = {
    verifiedUser?: {
        code: string;
        userId: string;
    };
};

type TResetPasswordContext = {
    resetPasswordUiState: EResetPasswordUiState;
    password: string;
    setPassword: (password: string) => void;
    setResetPasswordUiState: (resetPasswordUiState: EResetPasswordUiState) => void;
    disableSubmit: boolean;
    userID: string;
    userToken: string;
    submitResetPasswordForm: () => void;
};

export enum EResetPasswordUiState {
    default,
    pending,
    error,
    success,
    expired,
}

const ResetPasswordContext = createContext<TResetPasswordContext | undefined>(undefined);

export const ResetPasswordProvider: FC = ({ children }) => {
    const { gatewayAPIBaseUrl, setAppUiState } = useContext(GlobalContext);
    const [resetPasswordUiState, setResetPasswordUiState] = useState(EResetPasswordUiState.default);
    const [userID, setUserID] = useState('');
    const [userToken, setUserToken] = useState('');
    const [password, setPassword] = useState('');
    const [passwordIsValid, setPasswordIsValid] = useState(false);
    const [disableSubmit, setDisableSubmit] = useState(true);
    const { search: queryParams, state: routerState = {} }: { search: string; state: TResetPasswordRouterState } =
        useLocation();

    // get initial data either from query string or router state
    const getInitalData = () => {
        let parsedUserId = null;
        let parsedCode = null;
        if (routerState.verifiedUser) {
            const { userId, code } = routerState.verifiedUser;
            parsedUserId = userId;
            parsedCode = code;
        } else {
            const params = new URLSearchParams(queryParams);
            parsedUserId = params.get(EQueryStringParam.userId);
            parsedCode = params.get(EQueryStringParam.code);
        }
        // make sure we have a value for userId and code
        if (parsedUserId && parsedCode) {
            setUserID(parsedUserId);
            setUserToken(parsedCode);
        } else {
            console.error('Failed to get userId or code from query string or router state');
            setAppUiState(EAppUiState.error);
        }
    };

    // validate password
    const checkPasswordIsValid = () => {
        if (validatePassword(password)) {
            setPasswordIsValid(true);
        } else {
            if (passwordIsValid === true) {
                setPasswordIsValid(false);
            }
        }
    };

    // enable submit button
    const checkDisableSubmit = () => {
        if (password.length && passwordIsValid) {
            setDisableSubmit(false);
        } else {
            if (disableSubmit === false) {
                setDisableSubmit(true);
            }
        }
    };

    // submit reset password form
    const submitResetPasswordForm = async () => {
        const postObject: TResetPasswordPost = {
            password,
            token: userToken,
        };
        try {
            setResetPasswordUiState(EResetPasswordUiState.pending);
            await API.post.resetPassword(gatewayAPIBaseUrl, postObject, userID);
            setResetPasswordUiState(EResetPasswordUiState.success);
        } catch (error) {
            if (error.response && error.response.status === 400) {
                setResetPasswordUiState(EResetPasswordUiState.expired);
            } else {
                setResetPasswordUiState(EResetPasswordUiState.error);
            }
        }
    };

    // get initial data
    useEffect(() => {
        getInitalData();
    }, []);

    // check to enable submit button
    useEffect(() => {
        checkPasswordIsValid();
        checkDisableSubmit();
    }, [password, passwordIsValid]);

    return (
        <ResetPasswordContext.Provider
            value={{
                password,
                setPassword,
                resetPasswordUiState,
                setResetPasswordUiState,
                disableSubmit,
                userID,
                userToken,
                submitResetPasswordForm,
            }}
        >
            {children}
        </ResetPasswordContext.Provider>
    );
};

export default ResetPasswordContext;
